import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { listaPrecioService, ListaPrecio, ListaPrecioCreate, ListaPrecioUpdate } from '../../services/listaPrecioService';

interface ListaPrecioFormProps {
  lista?: ListaPrecio | null;
  onClose: () => void;
}

const ListaPrecioForm = ({ lista, onClose }: ListaPrecioFormProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ListaPrecioCreate | ListaPrecioUpdate>({
    defaultValues: lista ? {
      name: lista.name
    } : {
      name: ''
    }
  });

  useEffect(() => {
    if (lista) {
      reset({
        name: lista.name
      });
    }
  }, [lista, reset]);

  const createMutation = useMutation(listaPrecioService.create, {
    onSuccess: () => {
      toast.success('Lista de precios creada correctamente');
      queryClient.invalidateQueries(['listas-precios']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear lista de precios');
    }
  });

  const updateMutation = useMutation(
    (data: ListaPrecioUpdate) => listaPrecioService.update(lista!.id, data),
    {
      onSuccess: () => {
        toast.success('Lista de precios actualizada correctamente');
        queryClient.invalidateQueries(['listas-precios']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar lista de precios');
      }
    }
  );

  const onSubmit = (data: ListaPrecioCreate | ListaPrecioUpdate) => {
    if (lista) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data as ListaPrecioCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {lista ? 'Editar Lista de Precios' : 'Nueva Lista de Precios'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              {...register('name', { 
                required: 'Nombre es requerido',
                minLength: { value: 2, message: 'Mínimo 2 caracteres' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Guardando...'
                : lista
                ? 'Actualizar'
                : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListaPrecioForm;

